import {
  KubernetesNetworkSecurityFormValues,
  NetworkSecurityRule,
  PodSelection,
  PodSelectionType,
  TrafficSourceType,
} from '@/react/portainer/policies/components/kubernetes/network-security/types';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyTableWrapper } from './PolicyTableWrapper';

function SettingsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-1 overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
      {children}
    </div>
  );
}

function SettingItem({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10">
      <span className="text-sm">{label}</span>
      <Badge type={value ? 'success' : 'muted'}>
        {value ? 'Enabled' : 'Disabled'}
      </Badge>
    </div>
  );
}

export function NameCellReadOnly({
  name,
  sourceType,
}: {
  name: string | undefined;
  sourceType: TrafficSourceType | undefined;
}) {
  if (name === undefined) {
    return <span></span>;
  }

  const isAllow = sourceType !== TrafficSourceType.DenyAll;
  return (
    <span>
      {name}{' '}
      <Badge type={isAllow ? 'success' : 'danger'}>
        {isAllow ? 'ALLOW' : 'DENY'}
      </Badge>
    </span>
  );
}

export function AppliesToCellReadOnly({
  value,
}: {
  value: PodSelection | undefined;
}) {
  if (value === undefined) {
    return <span></span>;
  }

  const matchLabels: string[] = [];
  const matchExpressions: string[] = [];
  switch (value.type) {
    case PodSelectionType.All:
      return <span>All pods</span>;
    case PodSelectionType.MatchLabels:
      if (value.matchLabels) {
        for (const key of Object.keys(value.matchLabels)) {
          matchLabels.push(key + ': ' + value.matchLabels[key]);
        }
      }
      return <span>Labels: {matchLabels.join(', ')}</span>;
    case PodSelectionType.MatchExpression:
      if (value.matchExpressions) {
        for (const label of value.matchExpressions) {
          matchExpressions.push(
            label.key +
              ' ' +
              label.operator +
              ': (' +
              label.values.join(', ') +
              ')'
          );
        }
      }
      return <span>Match Expressions: {matchExpressions.join(', ')}</span>;
    default:
      return <span>Unknown</span>;
  }
}

export function PodLabelsCellReadOnly({
  labels,
}: {
  labels: Record<string, string> | undefined;
}) {
  if (labels === undefined) {
    return <span></span>;
  }
  const t: string[] = [];
  if (labels) {
    for (const key of Object.keys(labels)) {
      t.push(key + ': ' + labels[key]);
    }
  }
  const s = t.join(', ');
  return <span>{s}</span>;
}

export function RulesTableReadOnly({
  rules,
  kind,
}: {
  rules: NetworkSecurityRule[];
  kind: string;
}) {
  const caption = `${kind.charAt(0).toUpperCase()}${kind.slice(1)} rules`;
  const dataCY = `network-security-policy-${kind}-rules-readonly-table`;
  return (
    <PolicyTableWrapper caption={caption} data-cy={dataCY}>
      <thead className="bg-gray-3 th-highcontrast:bg-gray-9 th-dark:bg-gray-9">
        <tr>
          <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
          <th className="px-4 py-3 text-left text-sm font-medium">
            Applies To
          </th>
          <th className="px-4 py-3 text-left text-sm font-medium">Namespace</th>
          <th className="px-4 py-3 text-left text-sm font-medium">
            Pod Labels
          </th>
          <th className="px-4 py-3 text-left text-sm font-medium">Port</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-5 th-highcontrast:divide-gray-7 th-dark:divide-gray-7">
        {rules.map((rule) => (
          <tr
            key={rule.name || ''}
            className="bg-gray-2 th-highcontrast:bg-black th-dark:bg-gray-iron-10"
          >
            <td className="px-4 py-3 text-sm font-medium">
              <NameCellReadOnly name={rule.name} sourceType={rule.sourceType} />
            </td>
            <td className="px-4 py-3 text-sm font-medium">
              <AppliesToCellReadOnly value={rule.appliesTo} />
            </td>
            <td className="px-4 py-3 text-sm font-medium">
              {rule.namespace || ''}
            </td>
            <td className="px-4 py-3 text-sm font-medium">
              <PodLabelsCellReadOnly labels={rule.podLabels} />
            </td>
            <td className="px-4 py-3 text-sm font-medium">
              <Badge type="info">{rule.port || 'ANY'}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </PolicyTableWrapper>
  );
}

interface Props {
  formData: KubernetesNetworkSecurityFormValues;
}

export function NetworkSecurityPolicyReadOnly({ formData }: Props) {
  const ingressRules = formData.ingressRules || [];
  const egressRules = formData.egressRules || [];
  const namespaces = formData.namespaces || [];

  return (
    <div className="space-y-6">
      <FormSection title="Applied Namespaces">
        <SettingsList>
          {namespaces.map((namespace) => (
            <SettingItem
              key={namespace.namespace}
              label={namespace.namespace}
              value={true}
            />
          ))}
        </SettingsList>
      </FormSection>
      <FormSection title="Ingress Rules">
        <RulesTableReadOnly
          rules={ingressRules}
          kind="ingress"
        ></RulesTableReadOnly>
      </FormSection>
      <FormSection title="Egress Rules">
        <RulesTableReadOnly
          rules={egressRules}
          kind="egress"
        ></RulesTableReadOnly>
      </FormSection>
    </div>
  );
}
